import {
  useState,
  useMemo,
  useEffect,
  useRef,
  memo,
  type ReactElement,
  type RefObject,
} from 'react';
import ArrWaveform, {
  ArrWaveformEvents,
  ArrWaveformOptions,
} from './waveform/arrWaveform';

type ArrWaveformEventHandler<T extends unknown[]> = (
  arrwaveform: ArrWaveform,
  ...args: T
) => void;

type OnArrWaveformEvents = {
  [K in keyof ArrWaveformEvents as `on${Capitalize<K>}`]?: ArrWaveformEventHandler<
    ArrWaveformEvents[K]
  >;
};

type PartialArrWaveformOptions = Omit<ArrWaveformOptions, 'container'>;

export type ArrWaveformProps = PartialArrWaveformOptions & OnArrWaveformEvents;

function useArrWaveformInstance(
  containerRef: RefObject<HTMLDivElement | null>,
  options: Partial<ArrWaveformOptions>
): ArrWaveform | null {
  const [arrwaveform, setArrWaveform] = useState<ArrWaveform | null>(null);

  const flatOptions = useMemo(() => Object.entries(options).flat(), [options]);

  useEffect(() => {
    if (!containerRef?.current) return;

    const ws = ArrWaveform.create({
      ...options,
      container: containerRef.current,
    });

    setArrWaveform(ws);

    return () => {
      ws.destroy();
    };
  }, [containerRef, ...flatOptions]);

  return arrwaveform;
}

function useArrWaveformState(arrwaveform: ArrWaveform | null): {
  isReady: boolean;
  isPlaying: boolean;
  currentTime: number;
  duration?: number;
} {
  const [isReady, setIsReady] = useState<boolean>(false);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [hasFinished, setHasFinished] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number | undefined>();

  useEffect(() => {
    if (!arrwaveform) return;

    const unsubscribeFns = [
      arrwaveform.on('load', () => {
        setIsReady(false);
        setIsPlaying(false);
        setCurrentTime(0);
      }),

      arrwaveform.on('ready', (durationValue: number) => {
        arrwaveform?.setOptions({
          duration: durationValue,
        });
        setIsReady(true);
        setIsPlaying(false);
        setHasFinished(false);
        setCurrentTime(0);
        setDuration(durationValue);
      }),

      arrwaveform.on('finish', () => {
        setHasFinished(true);
      }),

      arrwaveform.on('play', () => {
        setIsPlaying(true);
      }),

      arrwaveform.on('pause', () => {
        setIsPlaying(false);
      }),

      arrwaveform.on('timeupdate', () => {
        setCurrentTime(arrwaveform.getCurrentTime());
      }),

      arrwaveform.on('destroy', () => {
        setIsReady(false);
        setIsPlaying(false);
      }),
    ];

    return () => {
      unsubscribeFns.forEach((fn) => fn());
    };
  }, [arrwaveform]);

  return useMemo(
    () => ({
      isReady,
      isPlaying,
      hasFinished,
      currentTime,
      duration,
    }),
    [isPlaying, hasFinished, currentTime, isReady, duration]
  );
}

const EVENT_PROP_RE = /^on([A-Z])/;
const isEventProp = (key: string) => EVENT_PROP_RE.test(key);
const getEventName = (key: string) =>
  key.replace(EVENT_PROP_RE, (_, $1) =>
    $1.toLowerCase()
  ) as keyof ArrWaveformEvents;

function useArrWaveformProps(
  props: ArrWaveformProps
): [PartialArrWaveformOptions, OnArrWaveformEvents] {
  return useMemo<[PartialArrWaveformOptions, OnArrWaveformEvents]>(() => {
    const allOptions = {...props};
    const allEvents = {...props};

    for (const key in allOptions) {
      if (isEventProp(key)) {
        delete allOptions[key as keyof ArrWaveformProps];
      } else {
        delete allEvents[key as keyof ArrWaveformProps];
      }
    }
    return [allOptions, allEvents];
  }, [props]);
}

function useArrWaveformEvents(
  arrwaveform: ArrWaveform | null,
  events: OnArrWaveformEvents
) {
  const flatEvents = useMemo(() => Object.entries(events).flat(), [events]);

  useEffect(() => {
    if (!arrwaveform) return;

    const eventEntries = Object.entries(events);
    if (!eventEntries.length) return;

    const unsubscribeFns = eventEntries.map(([name, handler]) => {
      const event = getEventName(name);
      return arrwaveform.on(event, (...args) =>
        (handler as ArrWaveformEventHandler<ArrWaveformEvents[typeof event]>)(
          arrwaveform,
          ...args
        )
      );
    });

    return () => {
      unsubscribeFns.forEach((fn) => fn());
    };
  }, [arrwaveform, ...flatEvents]);
}

const ArrWaveformPlayer = memo((props: ArrWaveformProps): ReactElement => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [options, events] = useArrWaveformProps(props);
  const arrwaveform = useArrWaveformInstance(containerRef, options);
  useArrWaveformEvents(arrwaveform, events);

  return <div ref={containerRef} data-testid={`use-arr-waveform-div`} />;
});

export default ArrWaveformPlayer;

export function useArrWaveform({
  container,
  ...options
}: Omit<ArrWaveformOptions, 'container'> & {
  container: RefObject<HTMLDivElement | null>;
}): ReturnType<typeof useArrWaveformState> & {
  arrwaveform: ReturnType<typeof useArrWaveformInstance>;
} {
  const arrwaveform = useArrWaveformInstance(container, options);
  const state = useArrWaveformState(arrwaveform);
  return useMemo(() => ({...state, arrwaveform}), [state, arrwaveform]);
}
